// src/pages/GoalsPage.tsx
//
// Lightweight goals management surface.
// Lists active goals with progress, supports create/edit/archive.
// Works without Freedom framework.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import {
  useGetGoalsQuery,
  useCreateGoalMutation,
  useUpdateGoalMutation,
  useArchiveGoalMutation,
} from '../api/goalsApi';
import { useCreateConversationMutation, useAddMessageMutation } from '../api/conversationsApi';
import { trackAssistantLaunched, trackAssistantMeetingLaunched } from '../services/aiAnalytics';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorState from '../components/common/ErrorState';
import Button from '../components/common/Button';
import type {
  Goal,
  GoalType,
  GoalPeriodType,
  GoalStatus,
  GoalTrackingStatus,
  CreateGoalRequest,
  GoalExecutionSummary,
} from '../types/api';

// ============================================================
// Config
// ============================================================

const PERIOD_LABELS: Record<GoalPeriodType, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
  custom: 'Custom',
};

const TYPE_LABELS: Record<GoalType, string> = {
  metric: 'Metric',
  initiative: 'Initiative',
};

const STATUS_LABELS: Record<GoalStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  archived: 'Archived',
};

const TRACKING_CONFIG: Record<
  GoalTrackingStatus,
  { label: string; color: string; bgColor: string }
> = {
  on_track: { label: 'On Track', color: 'text-green-700', bgColor: 'bg-green-100' },
  ahead:    { label: 'Ahead',    color: 'text-blue-700',  bgColor: 'bg-blue-100'  },
  behind:   { label: 'Behind',   color: 'text-red-700',   bgColor: 'bg-red-100'   },
  completed:{ label: 'Done',     color: 'text-gray-700',  bgColor: 'bg-gray-100'  },
  no_data:  { label: 'Not tracked yet',  color: 'text-gray-500',  bgColor: 'bg-gray-100'  },
};

// ============================================================
// Helpers
// ============================================================

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function progressBarColor(status: GoalTrackingStatus): string {
  if (status === 'ahead' || status === 'completed') return 'bg-blue-500';
  if (status === 'behind') return 'bg-red-400';
  return 'bg-green-500';
}

// ============================================================
// AI CTA helpers
// ============================================================

function aiCtaLabel(trackingStatus: GoalTrackingStatus): string {
  switch (trackingStatus) {
    case 'behind':    return 'Ask AI why this goal is behind';
    case 'on_track':  return 'Ask AI what to do next';
    case 'ahead':     return 'Ask AI what to do next';
    case 'completed': return 'Ask AI what to do next';
    case 'no_data':   return 'Ask AI how to move this forward';
  }
}

function buildGoalMeetingPrimer(goal: Goal): string {
  // Reuse the same rich context, but close with a meeting-specific prompt
  const base = buildGoalContextMessage(goal);
  // Strip the last line (the review-specific question) and replace with meeting direction
  const lines = base.split('\n');
  // Find the last non-empty line index and replace from there
  let lastContentIdx = lines.length - 1;
  while (lastContentIdx > 0 && lines[lastContentIdx].trim() === '') lastContentIdx--;
  lines[lastContentIdx] =
    'Please prepare concise talking points for discussing this goal in a team meeting: current status, what is working, what is blocking progress, and the specific actions the team should take.';
  return lines.join('\n');
}

function buildGoalContextMessage(goal: Goal): string {
  const exec = goal.executionSummary;
  const pct = goal.progress.progressPercent;
  const lines: string[] = [];

  lines.push(`**Goal Review: ${goal.title}**`);
  lines.push('');
  lines.push(`Type: ${goal.goalType} · Period: ${goal.periodType}`);
  lines.push(`Dates: ${goal.startDate.split('T')[0]} → ${goal.endDate.split('T')[0]}`);
  lines.push(`Status: ${goal.progress.trackingStatus.replace('_', ' ')}${pct != null ? ` · ${pct.toFixed(1)}% complete` : ''}`);

  if (goal.goalType === 'metric' && goal.currentValue != null && goal.targetValue != null) {
    const unit = goal.metricUnit ? ` ${goal.metricUnit}` : '';
    lines.push(`Metric: ${Number(goal.currentValue).toLocaleString()}${unit} of ${Number(goal.targetValue).toLocaleString()}${unit}${goal.metricName ? ` (${goal.metricName})` : ''}`);
    if (goal.progress.paceGap != null && goal.progress.paceGap !== 0) {
      lines.push(`Pace gap: ${goal.progress.paceGap > 0 ? '+' : ''}${goal.progress.paceGap.toFixed(1)}${unit} vs expected pace`);
    }
  }

  if (exec && exec.linkedTasks > 0) {
    lines.push('');
    lines.push('Execution summary:');
    lines.push(`- ${exec.linkedTasks} linked tasks · ${exec.completedTasks} completed`);
    if (exec.overdueTasks > 0)  lines.push(`- ${exec.overdueTasks} overdue`);
    if (exec.blockedTasks > 0)  lines.push(`- ${exec.blockedTasks} blocked`);
    if (exec.estimatedMinutesTotal > 0) {
      const totalH = Math.round(exec.estimatedMinutesTotal / 60);
      const doneH  = Math.round(exec.estimatedMinutesCompleted / 60);
      lines.push(`- ~${doneH}h completed of ~${totalH}h estimated`);
    }
  } else if (goal.goalType === 'initiative') {
    lines.push('');
    lines.push('No tasks are linked to this goal yet.');
  }

  if (goal.notes) {
    lines.push('');
    lines.push(`Notes: ${goal.notes}`);
  }

  lines.push('');
  // State-specific prompt direction
  switch (goal.progress.trackingStatus) {
    case 'behind':
      lines.push('Please help me understand: Why is this goal behind? What are the most likely causes, and what concrete steps should we take to catch up?');
      break;
    case 'on_track':
      lines.push('Please help me understand: What should we prioritize next to keep this goal on track and make the most progress this period?');
      break;
    case 'ahead':
      lines.push('Please help me understand: We are ahead on this goal. What should we focus on next to maintain momentum and finish strong?');
      break;
    case 'no_data':
      lines.push('Please help me understand: We have no progress data for this goal yet. How should we move it forward? What are the best first steps?');
      break;
    default:
      lines.push('Please review this goal and tell me: what is working, what is not, and what should we do next?');
  }

  return lines.join('\n');
}

// ============================================================
// GoalCard
// ============================================================

interface GoalCardProps {
  goal: Goal;
  onEdit: (goal: Goal) => void;
  onArchive: (id: string) => void;
  onUpdateValue: (goal: Goal) => void;
  onAskAI: (goal: Goal) => void;
  onMeetingAI: (goal: Goal) => void;
}

function GoalCard({ goal, onEdit, onArchive, onUpdateValue, onAskAI, onMeetingAI }: GoalCardProps) {
  const { progress } = goal;
  const exec: GoalExecutionSummary | undefined = goal.executionSummary;
  const tracking = TRACKING_CONFIG[progress.trackingStatus];
  const pct = progress.progressPercent;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
              {TYPE_LABELS[goal.goalType]}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">
              {PERIOD_LABELS[goal.periodType]}
            </span>
            {goal.status !== 'active' && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium">
                {STATUS_LABELS[goal.status]}
              </span>
            )}
          </div>
          <h3 className="text-base font-semibold text-gray-900 leading-snug">{goal.title}</h3>
          {goal.description && (
            <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{goal.description}</p>
          )}
        </div>
        {/* Tracking badge */}
        <span
          className={`flex-shrink-0 text-xs px-2 py-1 rounded-full font-medium ${tracking.bgColor} ${tracking.color}`}
        >
          {tracking.label}
        </span>
      </div>

      {/* Metric progress */}
      {goal.goalType === 'metric' && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">
              {goal.metricName || 'Progress'}
              {goal.metricUnit ? ` (${goal.metricUnit})` : ''}
            </span>
            <span className="font-medium text-gray-900">
              {goal.currentValue != null ? Number(goal.currentValue).toLocaleString() : '—'}
              {goal.targetValue != null && (
                <span className="text-gray-400 font-normal">
                  {' '}/ {Number(goal.targetValue).toLocaleString()}
                </span>
              )}
            </span>
          </div>
          {pct != null && (
            <div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-2 rounded-full transition-all ${progressBarColor(progress.trackingStatus)}`}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-gray-400">{pct.toFixed(1)}% complete</span>
                {progress.paceGap != null && progress.paceGap !== 0 && (
                  <span className={`text-xs ${progress.paceGap > 0 ? 'text-blue-600' : 'text-red-500'}`}>
                    {progress.paceGap > 0 ? '+' : ''}{Number(progress.paceGap.toFixed(1)).toLocaleString()} vs pace
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Initiative — task-based progress bar or fallback note */}
      {goal.goalType === 'initiative' && (
        exec && exec.linkedTasks > 0 ? (
          <div className="space-y-1">
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-2 rounded-full transition-all ${progressBarColor(progress.trackingStatus)}`}
                style={{ width: `${Math.min(exec.completionRate ?? 0, 100)}%` }}
              />
            </div>
            <div className="text-right text-xs text-gray-400">
              {(exec.completionRate ?? 0).toFixed(1)}% complete
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">
            Initiative — no tasks linked yet
            {goal.linkedProjectId ? ' · linked to project' : ''}
          </p>
        )
      )}

      {/* Unified execution summary — all goal types, when tasks are linked */}
      {exec && exec.linkedTasks > 0 && (
        <div className="flex items-center gap-3 text-xs py-1.5 px-3 bg-gray-50 rounded-lg">
          <span className="text-gray-500">{exec.linkedTasks} linked</span>
          <span className="text-green-600 font-medium">{exec.completedTasks} done</span>
          {exec.overdueTasks > 0 && (
            <span className="text-red-500 font-medium">{exec.overdueTasks} overdue</span>
          )}
          {exec.blockedTasks > 0 && (
            <span className="text-amber-500 font-medium">{exec.blockedTasks} blocked</span>
          )}
        </div>
      )}

      {/* Intervention hint — behind goals with actionable execution signals */}
      {progress.trackingStatus === 'behind' && exec && (exec.overdueTasks > 0 || exec.blockedTasks > 0) && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
          Action needed:{' '}
          {[
            exec.overdueTasks > 0 && `clear ${exec.overdueTasks} overdue task${exec.overdueTasks > 1 ? 's' : ''}`,
            exec.blockedTasks > 0 && `unblock ${exec.blockedTasks} task${exec.blockedTasks > 1 ? 's' : ''}`,
          ]
            .filter(Boolean)
            .join(' and ')}{' '}
          to get back on track.
        </p>
      )}

      {/* Period */}
      <div className="text-xs text-gray-400">
        {formatDate(goal.startDate)} → {formatDate(goal.endDate)}
      </div>

      {/* Actions */}
      <div className="pt-2 border-t border-gray-100 space-y-2">
        {/* Primary CTAs */}
        <div className="flex gap-2">
          {exec && exec.linkedTasks > 0 && (
            <Link
              to={`/tasks?goalId=${goal.id}&gn=${encodeURIComponent(goal.title)}`}
              className="flex-1 py-1.5 px-3 text-xs font-medium text-center text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
            >
              View linked work
            </Link>
          )}
          <button
            onClick={() => onAskAI(goal)}
            className={`${exec && exec.linkedTasks > 0 ? 'flex-1' : 'w-full'} py-1.5 px-3 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors`}
          >
            {aiCtaLabel(progress.trackingStatus)}
          </button>
        </div>
        {/* Secondary management actions */}
        <div className="flex items-center gap-3">
          {goal.goalType === 'metric' && goal.status === 'active' && (
            <button
              onClick={() => onUpdateValue(goal)}
              className="text-xs text-blue-600 hover:underline"
            >
              Update value
            </button>
          )}
          <button
            onClick={() => onMeetingAI(goal)}
            className="text-xs text-gray-500 hover:underline"
          >
            Discuss in meeting
          </button>
          <button
            onClick={() => onEdit(goal)}
            className="text-xs text-gray-500 hover:underline"
          >
            Edit
          </button>
          {goal.status !== 'archived' && (
            <button
              onClick={() => onArchive(goal.id)}
              className="text-xs text-gray-400 hover:text-red-500 hover:underline ml-auto"
            >
              Archive
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// CreateEditModal
// ============================================================

type FormMode = 'create' | 'edit' | 'update_value';

interface ModalProps {
  mode: FormMode;
  initial?: Partial<Goal>;
  workspaceId: string;
  onClose: () => void;
  onCreate: (req: CreateGoalRequest) => Promise<void>;
  onUpdate: (id: string, patch: Record<string, unknown>) => Promise<void>;
}

function GoalModal({ mode, initial, workspaceId, onClose, onCreate, onUpdate }: ModalProps) {
  const today = new Date().toISOString().split('T')[0];

  const [form, setForm] = useState({
    title: initial?.title ?? '',
    description: initial?.description ?? '',
    goalType: (initial?.goalType ?? 'metric') as GoalType,
    periodType: (initial?.periodType ?? 'weekly') as GoalPeriodType,
    startDate: initial?.startDate?.split('T')[0] ?? today,
    endDate: initial?.endDate?.split('T')[0] ?? today,
    metricName: initial?.metricName ?? '',
    metricUnit: initial?.metricUnit ?? '',
    targetValue: initial?.targetValue != null ? String(Number(initial.targetValue)) : '',
    currentValue: initial?.currentValue != null ? String(Number(initial.currentValue)) : '',
    notes: initial?.notes ?? '',
    status: (initial?.status ?? 'active') as GoalStatus,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.startDate || !form.endDate) { setError('Start and end dates are required'); return; }
    if (form.startDate > form.endDate) { setError('Start date must be before end date'); return; }

    setSaving(true);
    try {
      if (mode === 'create') {
        await onCreate({
          workspaceId,
          title: form.title.trim(),
          description: form.description.trim() || null,
          goalType: form.goalType,
          periodType: form.periodType,
          startDate: form.startDate,
          endDate: form.endDate,
          metricName: form.metricName.trim() || null,
          metricUnit: form.metricUnit.trim() || null,
          targetValue: form.targetValue ? Number(form.targetValue) : null,
          currentValue: form.currentValue ? Number(form.currentValue) : null,
          notes: form.notes.trim() || null,
        });
      } else if (mode === 'update_value') {
        await onUpdate(initial!.id!, {
          currentValue: form.currentValue ? Number(form.currentValue) : null,
        });
      } else {
        await onUpdate(initial!.id!, {
          title: form.title.trim(),
          description: form.description.trim() || null,
          periodType: form.periodType,
          status: form.status,
          startDate: form.startDate,
          endDate: form.endDate,
          metricName: form.metricName.trim() || null,
          metricUnit: form.metricUnit.trim() || null,
          targetValue: form.targetValue ? Number(form.targetValue) : null,
          currentValue: form.currentValue ? Number(form.currentValue) : null,
          notes: form.notes.trim() || null,
        });
      }
      onClose();
    } catch {
      setError('Failed to save goal. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === 'create' ? 'New Goal' :
    mode === 'update_value' ? 'Update Progress' :
    'Edit Goal';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          {mode !== 'update_value' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. Hit 100 leads this quarter"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                {mode === 'create' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                    <select
                      value={form.goalType}
                      onChange={(e) => setForm({ ...form, goalType: e.target.value as GoalType })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="metric">Metric</option>
                      <option value="initiative">Initiative</option>
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Period *</label>
                  <select
                    value={form.periodType}
                    onChange={(e) => setForm({ ...form, periodType: e.target.value as GoalPeriodType })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.entries(PERIOD_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>

                {mode === 'edit' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value as GoalStatus })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              {(form.goalType === 'metric' || initial?.goalType === 'metric') && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Metric name</label>
                    <input
                      type="text"
                      value={form.metricName}
                      onChange={(e) => setForm({ ...form, metricName: e.target.value })}
                      placeholder="e.g. Leads Generated"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                    <input
                      type="text"
                      value={form.metricUnit}
                      onChange={(e) => setForm({ ...form, metricUnit: e.target.value })}
                      placeholder="e.g. leads, $, %"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target</label>
                    <input
                      type="number"
                      value={form.targetValue}
                      onChange={(e) => setForm({ ...form, targetValue: e.target.value })}
                      placeholder="e.g. 100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current value</label>
                    <input
                      type="number"
                      value={form.currentValue}
                      onChange={(e) => setForm({ ...form, currentValue: e.target.value })}
                      placeholder="e.g. 42"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  placeholder="Context, blockers, or next steps…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          {/* Update value only */}
          {mode === 'update_value' && (
            <div>
              <p className="text-sm text-gray-600 mb-3">
                Update the current value for <strong>{initial?.metricName || initial?.title}</strong>
                {initial?.metricUnit ? ` (${initial.metricUnit})` : ''}.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current value</label>
              <input
                type="number"
                value={form.currentValue}
                onChange={(e) => setForm({ ...form, currentValue: e.target.value })}
                placeholder="e.g. 42"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              {initial?.targetValue != null && (
                <p className="text-xs text-gray-400 mt-1">
                  Target: {Number(initial.targetValue).toLocaleString()}
                  {initial.metricUnit ? ` ${initial.metricUnit}` : ''}
                </p>
              )}
            </div>
          )}
        </form>

        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60"
          >
            {saving ? 'Saving…' : mode === 'update_value' ? 'Update' : mode === 'create' ? 'Create Goal' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// GoalsPage
// ============================================================

type StatusFilter = 'active' | 'all';

export default function GoalsPage() {
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [modal, setModal] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; goal: Goal }
    | { mode: 'update_value'; goal: Goal }
    | null
  >(null);

  const { data, isLoading, error, refetch } = useGetGoalsQuery(
    {
      workspaceId,
      status: statusFilter === 'active' ? 'active' : undefined,
    },
    { skip: !workspaceId }
  );

  const navigate = useNavigate();
  const [createGoal] = useCreateGoalMutation();
  const [updateGoal] = useUpdateGoalMutation();
  const [archiveGoal] = useArchiveGoalMutation();
  const [createConversation] = useCreateConversationMutation();
  const [addMessage] = useAddMessageMutation();

  const handleDiscussInMeeting = async (goal: Goal) => {
    if (!workspaceId) return;
    trackAssistantMeetingLaunched('goal', goal.id);
    try {
      const result = await createConversation({
        workspaceId,
        title: `Meeting: ${goal.title}`,
        mode: 'chat',
      }).unwrap();
      const sessionId = result.session.id;
      try {
        await addMessage({ sessionId, content: buildGoalMeetingPrimer(goal) }).unwrap();
      } catch {
        // context send failed — still navigate
      }
      navigate(`/conversations/${sessionId}?assistant=true`);
    } catch {
      // Non-critical — silently ignore
    }
  };

  const handleAskAI = async (goal: Goal) => {
    if (!workspaceId) return;
    trackAssistantLaunched('goal', goal.id, goal.progress.trackingStatus);
    try {
      const result = await createConversation({
        workspaceId,
        title: `Goal review: ${goal.title}`,
        mode: 'chat',
      }).unwrap();
      const sessionId = result.session.id;
      // Send rich context as first message so the AI has everything it needs
      try {
        await addMessage({ sessionId, content: buildGoalContextMessage(goal) }).unwrap();
      } catch {
        // Context send failed — still navigate, AI will work with less context
      }
      navigate(`/conversations/${sessionId}?assistant=true`);
    } catch {
      // Non-critical — silently ignore
    }
  };

  const handleCreate = async (req: CreateGoalRequest) => {
    await createGoal(req).unwrap();
  };

  const handleUpdate = async (id: string, patch: Record<string, unknown>) => {
    await updateGoal({ id, ...patch } as Parameters<typeof updateGoal>[0]).unwrap();
  };

  const handleArchive = async (id: string) => {
    if (!confirm('Archive this goal?')) return;
    await archiveGoal(id).unwrap();
  };

  if (!activeWorkspace) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a workspace to view goals.</p>
      </div>
    );
  }

  const goals = data?.goals ?? [];

  // Group by status for display
  const active = goals.filter((g) => g.status === 'active');
  const other = goals.filter((g) => g.status !== 'active');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Goals</h1>
          <p className="text-sm text-gray-500">
            Track targets and initiatives across any time horizon
          </p>
        </div>
        <Button onClick={() => setModal({ mode: 'create' })}>New Goal</Button>
      </div>

      {/* Filter strip */}
      <div className="flex gap-2">
        {(['active', 'all'] as StatusFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setStatusFilter(f)}
            className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
              statusFilter === f
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f === 'active' ? 'Active' : 'All'}
          </button>
        ))}
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <ErrorState message="Failed to load goals." onRetry={refetch} />
      ) : goals.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No goals yet</h3>
          <p className="text-gray-500 text-sm mb-4">
            Create your first goal to start tracking targets and initiatives.
          </p>
          <Button onClick={() => setModal({ mode: 'create' })}>New Goal</Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Active goals */}
          {active.length > 0 && (
            <div>
              {statusFilter === 'all' && (
                <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
                  Active ({active.length})
                </h2>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {active.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    onEdit={(g) => setModal({ mode: 'edit', goal: g })}
                    onArchive={handleArchive}
                    onUpdateValue={(g) => setModal({ mode: 'update_value', goal: g })}
                    onAskAI={handleAskAI}
                    onMeetingAI={handleDiscussInMeeting}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Non-active goals (only when showing all) */}
          {statusFilter === 'all' && other.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Other ({other.length})
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {other.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    onEdit={(g) => setModal({ mode: 'edit', goal: g })}
                    onArchive={handleArchive}
                    onUpdateValue={(g) => setModal({ mode: 'update_value', goal: g })}
                    onAskAI={handleAskAI}
                    onMeetingAI={handleDiscussInMeeting}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {modal && (
        <GoalModal
          mode={modal.mode}
          initial={modal.mode !== 'create' ? modal.goal : undefined}
          workspaceId={workspaceId!}
          onClose={() => setModal(null)}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
        />
      )}
    </div>
  );
}
